// @flow

import React from 'react';
import _ from 'lodash';
import { Tracking } from 'web-core/src/tracking';

import AccountForm from 'client/components/account/accountForm';
import FormErrorNotification from 'client/components/form/formErrorNotification';
import Hero from 'client/components/account/hero';
import LinkReplace from 'client/components/button/linkReplace';
import Translate from 'client/components/translate';
import PROVISION_ENABLE from 'client/constants/enterpriseProvision';
import { handle422Error, handleRedirect } from 'routes/join/utils/index';
import { WEB_CLICK_PROVISION } from 'routes/join/constants/tracking';
import type { LocationType, RouterType } from 'types/react-router.flow';

const PARTNER_PREFIX = 'partner.enterprise.account.login';
const COMMON_PREFIX = 'common.account.login';

type Props = {
  brand: string,
  code: string | null,
  id: string | null,
  isProvisioned: boolean,
  location: LocationType,
  login: Function,
  loginAndProvision: Function,
  redirect: ?string,
  router: RouterType,
  siteConfigs: Object,
  translations: Object,
};

type State = {
  error: string | null,
  isSubmitting: boolean,
};

type LoginHandlerType = {
  email: string,
  password: string,
};

class LoginPage extends React.PureComponent<Props, State> {
  static defaultProps = {
    code: null,
    id: null,
  };

  state = {
    error: null,
    isSubmitting: false,
  };

  bindLoginHandler = ({ email, password }: LoginHandlerType) => {
    const { brand, code, id, isProvisioned, redirect, router, translations } = this.props;
    this.setSubmitting(true);
    // Using loginAndProvision when no redirect route or provision for that route is enabled
    const loginUser =
      !redirect || PROVISION_ENABLE[redirect] ? this.props.loginAndProvision : this.props.login;
    const source = brand === 'stride' ? brand : `enterprise-${brand}`;

    return loginUser({
      email,
      password,
      code,
      source,
      id,
    })
      .then((data) => {
        // loginAndProvision & login return different data structure
        const user_id = _.get(data, 'oid', _.get(data, 'data.user.oid'));
        handleRedirect(redirect, user_id, data, router);
      })
      .catch((err) => {
        const serverError = _.get(err, 'response.status');
        if (serverError === 422) {
          // If bad code or no code provided
          handle422Error(isProvisioned, err, router, redirect);
        } else {
          const loginError = _.get(err, 'status') === 'ERROR';
          this.setState({
            error: _.get(translations, `common.error.${loginError ? 'loginFail' : 'generic'}`),
            isSubmitting: false,
          });
        }
      })
      .finally(() => {
        // enable tracking if no redirect defined or provision for route is enabled
        if (!redirect || PROVISION_ENABLE[redirect]) {
          Tracking.track(WEB_CLICK_PROVISION, { type: 'login', client: brand });
        }
      });
  };

  goToPasswordReset = (e: SyntheticEvent<>) => {
    e.preventDefault();
    window.location.href = '/account/request-reset';
  };

  setSubmitting = (isSubmitting: boolean) => {
    this.setState({ isSubmitting });
  };

  render() {
    const { brand, location, siteConfigs } = this.props;
    const { error, isSubmitting } = this.state;
    return (
      <div className="account-page text-center">
        <Hero brandName={brand} siteConfigs={siteConfigs} />
        {this.state.error && (
          <FormErrorNotification
            translateKey="partner.enterprise.error"
            translateUsing={{ errorMessage: error }} />
        )}
        <div className="center-col-small">
          <div className="margin-2">
            <h4 className="semi-bold">
              <Translate path={`${PARTNER_PREFIX}.header`} />
            </h4>
          </div>
          <AccountForm
            formType="login"
            isSubmitting={isSubmitting}
            submitHandler={this.bindLoginHandler} />
          <p className="mini margin-top">
            <Translate path={`${COMMON_PREFIX}.legal`} />
          </p>
          <button className="accessible-link margin" onClick={this.goToPasswordReset}>
            <Translate path={`${COMMON_PREFIX}.resetPassword`} />
          </button>
          <p className="pad-bottom">
            <Translate path={`${COMMON_PREFIX}.changeCta`} />
            <LinkReplace
              to={{
                pathname: '/create',
                query: location.query,
              }}>
              <Translate path={`${COMMON_PREFIX}.changeCtaLink`} />
            </LinkReplace>
          </p>
        </div>
      </div>
    );
  }
}

export default LoginPage;
